(() => {
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  const slideDistance = (scroller, selector) => {
    const slide = scroller?.querySelector(selector);
    if (!slide) return 0;
    const gap = Number.parseFloat(getComputedStyle(scroller).columnGap) || 0;
    return slide.getBoundingClientRect().width + gap;
  };

  const enableMouseDrag = (scroller) => {
    if (!scroller) return;

    let pointerId = null;
    let startX = 0;
    let startScrollLeft = 0;
    let dragged = false;
    let suppressClick = false;

    const stop = (event) => {
      if (pointerId === null || (event && event.pointerId !== pointerId)) return;
      suppressClick = dragged;
      pointerId = null;
      dragged = false;
      scroller.classList.remove("is-dragging");
    };

    scroller.addEventListener("dragstart", (event) => event.preventDefault());
    scroller.addEventListener("pointerdown", (event) => {
      if (event.pointerType === "touch" || event.button !== 0) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startScrollLeft = scroller.scrollLeft;
      dragged = false;
      suppressClick = false;
    });
    scroller.addEventListener("pointermove", (event) => {
      if (event.pointerId !== pointerId) return;
      const distance = event.clientX - startX;
      if (!dragged && Math.abs(distance) <= 10) return;
      if (!dragged) {
        dragged = true;
        scroller.classList.add("is-dragging");
        scroller.setPointerCapture?.(pointerId);
      }
      event.preventDefault();
      scroller.scrollLeft = startScrollLeft - distance;
    });
    scroller.addEventListener("pointerup", stop);
    scroller.addEventListener("pointercancel", stop);
    scroller.addEventListener("lostpointercapture", stop);
    scroller.addEventListener(
      "click",
      (event) => {
        if (!suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
        suppressClick = false;
      },
      true,
    );
  };

  const nearestIndex = (scroller) => {
    const slides = [...scroller.children];
    if (!slides.length) return 0;
    return slides.reduce((nearest, slide, index) => {
      const previous = Math.abs(slides[nearest].offsetLeft - scroller.scrollLeft);
      const current = Math.abs(slide.offsetLeft - scroller.scrollLeft);
      return current < previous ? index : nearest;
    }, 0);
  };

  const initRoot = (root) => {
    if (root.dataset.ao248StoryReady === "true") return;
    root.dataset.ao248StoryReady = "true";

    const sceneCarousel = root.querySelector("[data-carousel]");
    enableMouseDrag(sceneCarousel);
    root.querySelectorAll("[data-carousel-direction]").forEach((button) => {
      button.addEventListener("click", () => {
        const distance = slideDistance(sceneCarousel, ".scene-slide");
        sceneCarousel?.scrollBy({
          left: button.dataset.carouselDirection === "next" ? distance : -distance,
          behavior: reducedMotion.matches ? "auto" : "smooth",
        });
      });
    });

    const workflow = root.querySelector("[data-workflow-carousel]");
    const workflowCurrent = root.querySelector("[data-workflow-current]");
    const workflowButtons = [...root.querySelectorAll("[data-workflow-direction]")];
    enableMouseDrag(workflow);

    const updateWorkflow = () => {
      if (!workflow) return;
      const index = nearestIndex(workflow);
      if (workflowCurrent) workflowCurrent.textContent = String(index + 1).padStart(2, "0");
      workflowButtons.forEach((button) => {
        button.disabled = button.dataset.workflowDirection === "previous"
          ? index === 0
          : index === workflow.children.length - 1;
      });
    };

    workflowButtons.forEach((button) => {
      button.addEventListener("click", () => {
        const distance = slideDistance(workflow, ".workflow-step");
        workflow?.scrollBy({
          left: button.dataset.workflowDirection === "next" ? distance : -distance,
          behavior: reducedMotion.matches ? "auto" : "smooth",
        });
      });
    });

    if (workflow) {
      let workflowFrame;
      workflow.addEventListener("scroll", () => {
        cancelAnimationFrame(workflowFrame);
        workflowFrame = requestAnimationFrame(updateWorkflow);
      });
      updateWorkflow();
    }

    const voice = root.querySelector("[data-voice-carousel]");
    const voiceButtons = [...root.querySelectorAll("[data-voice-direction]")];
    enableMouseDrag(voice);

    const updateVoice = () => {
      if (!voice) return;
      const max = Math.max(0, voice.scrollWidth - voice.clientWidth);
      voiceButtons.forEach((button) => {
        button.disabled = button.dataset.voiceDirection === "previous"
          ? voice.scrollLeft <= 2
          : voice.scrollLeft >= max - 2;
      });
    };

    const moveVoice = (direction) => {
      const distance = slideDistance(voice, ".voice-card");
      voice?.scrollBy({
        left: direction === "next" ? distance : -distance,
        behavior: reducedMotion.matches ? "auto" : "smooth",
      });
    };

    voiceButtons.forEach((button) => {
      button.addEventListener("click", () => moveVoice(button.dataset.voiceDirection));
    });

    if (voice) {
      let voiceFrame;
      voice.addEventListener("scroll", () => {
        cancelAnimationFrame(voiceFrame);
        voiceFrame = requestAnimationFrame(updateVoice);
      });
      voice.addEventListener("keydown", (event) => {
        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
        event.preventDefault();
        moveVoice(event.key === "ArrowRight" ? "next" : "previous");
      });
      window.addEventListener("resize", updateVoice, { passive: true });
      updateVoice();
    }

    const storyLinks = [...root.querySelectorAll("[data-story-target]")];
    const storySections = storyLinks
      .map((link) => root.querySelector(`#${CSS.escape(link.dataset.storyTarget)}`))
      .filter(Boolean);

    const setActiveLink = (sectionId) => {
      storyLinks.forEach((link) => {
        if (link.dataset.storyTarget === sectionId) {
          link.setAttribute("aria-current", "location");
          link.closest(".story-nav__inner")?.scrollTo({
            left: link.offsetLeft - (link.parentElement.clientWidth - link.offsetWidth) / 2,
            behavior: reducedMotion.matches ? "auto" : "smooth",
          });
        } else {
          link.removeAttribute("aria-current");
        }
      });
    };

    storyLinks.forEach((link) => {
      link.addEventListener("click", () => setActiveLink(link.dataset.storyTarget));
    });

    if (storySections.length && "IntersectionObserver" in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((first, second) => second.intersectionRatio - first.intersectionRatio)[0];
          if (visible) setActiveLink(visible.target.id);
        },
        { rootMargin: "-20% 0px -65% 0px", threshold: [0, 0.01, 0.5] },
      );
      storySections.forEach((section) => observer.observe(section));
    }

    root.querySelector("[data-ao248-return-to-purchase]")?.addEventListener("click", (event) => {
      const purchaseSection = document.querySelector(".shopify-section--main-product");
      if (!purchaseSection) return;
      event.preventDefault();
      purchaseSection.scrollIntoView({ behavior: reducedMotion.matches ? "auto" : "smooth", block: "start" });
      window.setTimeout(() => {
        purchaseSection.querySelector('button[type="submit"]:not([disabled])')?.focus({ preventScroll: true });
      }, reducedMotion.matches ? 0 : 450);
    });
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll?.("[data-ao248-story-b]").forEach(initRoot);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
})();
